import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import Dropdown from '@components/common/formControl/dropdown/dropdown';
import TextInput from '@components/common/formControl/textInput/textInput';
import { useFccSlabQuery } from '@services/hooks/contractor360/useFetchFccSlabDetails';
import { useFccSlabMutation } from '@services/hooks/contractor360/useFccEnrollMutation';
import { ToastType, useTosterStore } from '@store/toster';
import { useFccEnrollUserQuery } from '@services/hooks/contractor360/useFetchFccEnrollUser';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useFccCheckQuery } from '@services/hooks/contractor360/useFccCheckQuery';
import { maxFileSize } from '@config/constant';
import { usePresignedURLQuery } from '@services/hooks/enduser-details/usePresignedURLQuery';
import { useCommonImageUpload } from '@services/hooks/commonImageUpload';

export interface FCCModalDetailsProps {
	membershipData: { number: string; name: string };
	onClose: () => void;
}

export const AddFccCheckModal: React.FC<FCCModalDetailsProps> = (props) => {
	const { membershipData, onClose } = props;
	const { data } = useFccSlabQuery();
	const {
		data: fccStatus,
		refetch: statusRefetch,
		isFetching: loadingDetails,
	} = useFccCheckQuery(+membershipData?.number);
	const { mutate, isPending } = useFccSlabMutation(fccStatus?.fpc_id);
	const { setToaster } = useTosterStore();
	const { data: FccUser, isFetching, error, refetch: loadDetails } = useFccEnrollUserQuery(fccStatus?.fpc_id);
	const { mutate: mutatePresignedURL, data: getPresignedURL } = usePresignedURLQuery();
	const { mutate: imageUploadMutate, isPending: isImageUploadPending } = useCommonImageUpload();

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewImage, setPreviewImage] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const dropDownOptions = useMemo(() => {
		return data?.data?.map((item) => ({ value: item, label: item, id: item })) ?? [];
	}, [data?.data]);

	const formik = useFormik({
		initialValues: {
			contractorName: membershipData.name,
			fpcSlab: '',
			contractorNumber: membershipData.number,
			imageUrl: '',
		},
		onSubmit: () => {
			handleSubmit();
		},
	});

	const handleFCCSubmit = (imageUrlParam?: string): void => {
		const imageUrl = imageUrlParam ?? formik.values.imageUrl ?? '';
		const payload = {
			contractor_name: formik.values.contractorName,
			contractor_membershipno: formik.values.contractorNumber,
			fpc_slab: formik.values.fpcSlab,
			image_url: imageUrl,
		};
		mutate(fccStatus?.fpc_id ? { fpc_slab: formik.values.fpcSlab, image_url: imageUrl } : payload, {
			onSuccess: (data) => {
				if (data) {
					formik.resetForm();
					onClose();
					statusRefetch?.();
					setToaster(true, ToastType.success, `Fpc ${fccStatus?.fpc_id ? 'updated' : 'enrolled'}  successfully`);
				}
			},
			onError: () => {
				setToaster(true, ToastType.error, 'Unable to enroll fpc user');
			},
		});
	};

	const handleSubmit = useCallback(() => {
		if (selectedFile && getPresignedURL?.[0]?.pre_signed_url) {
			const imageUploadPayload = {
				url: getPresignedURL[0].pre_signed_url,
				imageSource: selectedFile,
			};

			imageUploadMutate(imageUploadPayload, {
				onSuccess: () => {
					formik.setFieldValue('imageUrl', getPresignedURL[0].pre_signed_url);
					handleFCCSubmit(getPresignedURL[0].pre_signed_url);
				},
				onError: () => {
					setToaster(true, ToastType.error, 'Image upload failed');
				},
			});
		} else {
			handleFCCSubmit();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik, getPresignedURL, imageUploadMutate, selectedFile]);

	useEffect(() => {
		if (!loadingDetails && fccStatus?.fpc_id) {
			loadDetails?.();
		}
	}, [fccStatus, loadDetails, loadingDetails]);

	useEffect(() => {
		if (FccUser) {
			formik.setValues(
				{
					contractorNumber: FccUser?.contractor_membershipno ?? '',
					contractorName: FccUser?.contractor_name ?? '',
					fpcSlab: FccUser?.fpc_slab ?? '',
					imageUrl: FccUser?.image_url ?? '',
				},
				true
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [FccUser]);

	const handleClose = useCallback(() => {
		formik.resetForm();
		onClose();
	}, [formik, onClose]);

	const handleFileUpload = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, folder: string): void => {
			const file = event.target.files?.[0] as File;
			if (!file) return;

			if (!file.type.startsWith('image/')) {
				setToaster?.(true, ToastType.error, `Please upload an image file.`);
				event.target.value = '';
				return;
			}

			if (maxFileSize && file.size > maxFileSize) {
				setToaster?.(true, ToastType.error, `File size exceeds 5MB limit. Please choose a smaller file.`);
				event.target.value = '';
				return;
			}

			const payload = [{ folder, filename: file.name }];
			mutatePresignedURL(payload);

			const previewURL = URL.createObjectURL(file);
			formik.setFieldValue('imageUrl', previewURL);
			setSelectedFile(file);
			setPreviewImage(previewURL);
		},
		[formik, mutatePresignedURL, setToaster]
	);

	const handleImageChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			handleFileUpload(event, 'fpc_users');
		},
		[handleFileUpload]
	);

	const handleRemoveImage = (): void => {
		setSelectedFile(null);
		setPreviewImage(null);
		if (!formik.initialValues.imageUrl || previewImage) {
			formik.setFieldValue('imageUrl', '');
		}
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const isSubmitDisabled = !formik.isValid || !formik.dirty || isPending || isFetching || loadingDetails;
	return (
		<form onSubmit={formik.handleSubmit}>
			<div className="popup-content">
				<Wrapper isLoaderVisible={isFetching || loadingDetails} isError={error}>
					<div className="input-container">
						<div className="form-control">
							<TextInput
								id="contractorNumber"
								name="contractorNumber"
								onBlur={formik.handleBlur}
								type="text"
								value={formik.values.contractorNumber ?? ''}
								setFieldValue={formik.setFieldValue}
								label="Contractor Membership Number"
								error={
									formik.touched.contractorNumber && formik.errors.contractorNumber
										? formik.errors.contractorNumber
										: null
								}
								required
								disabled
							/>
						</div>
						<div className="form-control margin-content">
							<TextInput
								id="contractorName"
								name="contractorName"
								onBlur={formik.handleBlur}
								type="text"
								value={formik.values.contractorName ?? ''}
								setFieldValue={formik.setFieldValue}
								label="Contractor Name"
								error={
									formik.touched.contractorName && formik.errors.contractorName ? formik.errors.contractorName : null
								}
								required
								disabled
							/>
						</div>

						<div className="form-control margin-content">
							<Dropdown
								id="fpcSlab"
								name="fpcSlab"
								label="FPC Slab to be enrolled in"
								title={formik.values.fpcSlab ?? ''}
								error={formik.touched.fpcSlab && formik.errors.fpcSlab ? formik.errors.fpcSlab : null}
								options={dropDownOptions}
								onBlur={formik.handleBlur}
								setFieldValue={formik.setFieldValue}
								required
							/>
						</div>

						<div className="form-control margin-content fcc-image-wrapper">
							{formik.values.imageUrl && (
								<div className="preview-img">
									<img src={previewImage || formik.values.imageUrl} alt="fcc img" title="fcc img" />
									<button type="button" onClick={handleRemoveImage} aria-label="Remove image">
										<span className="icon-cross"></span>
									</button>
								</div>
							)}

							<div>
								<label htmlFor="image-upload">
									<span className="hide">image upload</span>
								</label>
								<input
									ref={fileInputRef}
									type="file"
									name="image-url"
									id="image-url"
									accept="image/*"
									onChange={handleImageChange}
									disabled={isImageUploadPending}
								/>
							</div>
						</div>
					</div>
				</Wrapper>
			</div>
			<div className="btn-wrapper">
				<button
					className="btn btn-primary btn-cancel close"
					type="button"
					aria-label="cancel-button"
					onClick={handleClose}
					disabled={isPending || isImageUploadPending}
				>
					Cancel
				</button>
				<button className="btn btn-primary" type="submit" disabled={isSubmitDisabled} aria-label="submit-button">
					Submit
				</button>
			</div>
		</form>
	);
};
